import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL || '';

export default function ReviewerAccessPage() {
  const navigate = useNavigate();
  const [step, setStep] = useState('landing'); // landing | form | loading | done | error
  const [email, setEmail] = useState('');
  const [name, setName] = useState('');
  const [company, setCompany] = useState('');
  const [error, setError] = useState('');

  const handleStart = async (e) => {
    e.preventDefault();
    if (!email || !name) { setError('Please enter your name and email.'); return; }
    setError('');
    setStep('loading');

    try {
      const res = await fetch(`${BACKEND_URL}/api/auth/reviewer-access`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify({ email, name, company })
      });
      const data = await res.json();

      if (data.success) {
        // Auto-redirect into the app
        navigate(`/app?reviewer=1&welcome=1`);
      } else {
        // Already registered — redirect to login
        if (data.existing) {
          navigate(`/app?action=login&reason=reviewer`);
        } else {
          setError(data.message || 'Something went wrong. Please try again.');
          setStep('form');
        }
      }
    } catch (err) {
      setError('Connection error. Please try again.');
      setStep('form');
    }
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-green-600 via-green-700 to-emerald-800 flex flex-col">

      {/* Header */}
      <div className="flex items-center justify-between px-6 py-4">
        <div className="flex items-center gap-2">
          <span className="text-2xl">🛒</span>
          <span className="text-white font-bold text-xl">PricePAL Compare</span>
        </div>
        <span className="bg-white/20 text-white text-xs font-semibold px-3 py-1 rounded-full">
          Reviewer Access
        </span>
      </div>

      {/* Main content */}
      <div className="flex-1 flex items-center justify-center px-4 py-12">
        <div className="w-full max-w-lg">

          {/* Landing step */}
          {step === 'landing' && (
            <div className="text-center text-white">
              <div className="text-6xl mb-4">🎯</div>
              <h1 className="text-4xl font-bold mb-3">Reviewer Access</h1>
              <p className="text-green-100 text-lg mb-2">Full 7-Day Free Trial — No payment needed</p>
              <p className="text-green-200 text-sm mb-10">
                Access the complete PricePAL Compare app instantly. Compare grocery prices across 10 UK supermarkets, scan receipts, track fuel prices and more.
              </p>

              {/* Feature highlights */}
              <div className="grid grid-cols-2 gap-3 mb-10 text-left">
                {[
                  { icon: '🛒', text: '10 UK supermarkets compared' },
                  { icon: '📷', text: 'AI receipt scanner' },
                  { icon: '⛽', text: 'Live fuel prices' },
                  { icon: '🗓️', text: 'Best day to shop' },
                  { icon: '🔔', text: 'Price drop alerts' },
                  { icon: '🍳', text: 'Kitchen AI meal planner' },
                ].map((f, i) => (
                  <div key={i} className="bg-white/10 rounded-xl px-4 py-3 flex items-center gap-2">
                    <span className="text-xl">{f.icon}</span>
                    <span className="text-white text-sm font-medium">{f.text}</span>
                  </div>
                ))}
              </div>

              <button
                onClick={() => setStep('form')}
                className="w-full bg-white text-green-700 font-bold text-lg py-4 rounded-2xl hover:bg-green-50 transition shadow-xl"
              >
                Get Instant Access →
              </button>
              <p className="text-green-300 text-xs mt-3">No credit card · No commitment · Cancel anytime</p>
            </div>
          )}

          {/* Form step */}
          {step === 'form' && (
            <div className="bg-white rounded-3xl shadow-2xl p-8">
              <div className="text-center mb-6">
                <div className="text-4xl mb-2">🚀</div>
                <h2 className="text-2xl font-bold text-gray-800">Start Your Free Trial</h2>
                <p className="text-gray-500 text-sm mt-1">Full access · 7 days free · No card needed</p>
              </div>

              <form onSubmit={handleStart} className="space-y-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Full Name</label>
                  <input
                    type="text"
                    value={name}
                    onChange={e => setName(e.target.value)}
                    placeholder="Jane Smith"
                    required
                    className="w-full border border-gray-300 rounded-xl px-4 py-3 text-sm focus:ring-2 focus:ring-green-500 focus:border-transparent outline-none"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Work Email</label>
                  <input
                    type="email"
                    value={email}
                    onChange={e => setEmail(e.target.value)}
                    placeholder="jane@yourcompany.com"
                    required
                    className="w-full border border-gray-300 rounded-xl px-4 py-3 text-sm focus:ring-2 focus:ring-green-500 focus:border-transparent outline-none"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Company / Network <span className="text-gray-400 font-normal">(optional)</span>
                  </label>
                  <input
                    type="text"
                    value={company}
                    onChange={e => setCompany(e.target.value)}
                    placeholder="Your company / publication"
                    className="w-full border border-gray-300 rounded-xl px-4 py-3 text-sm focus:ring-2 focus:ring-green-500 focus:border-transparent outline-none"
                  />
                </div>

                {error && (
                  <div className="bg-red-50 border border-red-200 text-red-700 text-sm px-4 py-3 rounded-xl">
                    {error}
                  </div>
                )}

                <button
                  type="submit"
                  className="w-full bg-green-600 text-white font-bold text-lg py-4 rounded-2xl hover:bg-green-700 transition"
                >
                  Access the App Now →
                </button>

                <p className="text-center text-xs text-gray-400">
                  By continuing you agree to our{' '}
                  <a href="/terms" className="underline text-gray-500">Terms</a> and{' '}
                  <a href="/privacy" className="underline text-gray-500">Privacy Policy</a>.
                </p>
              </form>
            </div>
          )}

          {/* Loading step */}
          {step === 'loading' && (
            <div className="text-center text-white">
              <div className="w-16 h-16 border-4 border-white/30 border-t-white rounded-full animate-spin mx-auto mb-6"></div>
              <h2 className="text-2xl font-bold mb-2">Setting up your trial...</h2>
              <p className="text-green-200">You'll be inside the app in seconds</p>
            </div>
          )}

        </div>
      </div>

      {/* Footer */}
      <div className="text-center text-green-300 text-xs pb-6">
        © 2026 PricePAL Compare · ICO: ZC094153 · CRN: 17031232
      </div>
    </div>
  );
}
